import React, { useState, useRef, useEffect } from 'react';
import AttachmentSelector from './AttachmentSelector';
import './QuickChat.css';

/**
 * QuickChat Component
 * Unified chat interface supporting both quick chat and order-based chat
 * with selectable data sources (attachments) and processing modes
 *
 * @param {string} orderId - Optional order/property ID for context-aware chat
 * @param {function} activeTab - Current active tab
 * @param {function} onTabChange - Tab change handler
 */
function QuickChat({ orderId = null, activeTab, onTabChange }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('oracle'); // Default to Oracle
  const [error, setError] = useState(null);
  const [oracleRounds, setOracleRounds] = useState([]); // Track oracle conversation rounds

  // Attachment (data source) selection
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [availableSources, setAvailableSources] = useState({});

  // Prompt preview modal
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [promptPreview, setPromptPreview] = useState('');

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const longPressTimer = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Fetch available data sources when orderId changes
  useEffect(() => {
    if (orderId) {
      fetchAvailableSources();
    } else {
      // No order selected - clear order-based attachments
      setAvailableSources({});
      setAttachments(prev => prev.filter(att => att.type === 'uploadFile'));
    }
  }, [orderId]);

  const fetchAvailableSources = async () => {
    try {
      // Fetch order data to determine which sources are available
      const response = await fetch(`/api/orders/${orderId}`);
      if (response.ok) {
        const orderData = await response.json();

        // Determine which sources have data
        const available = {
          orderEntry: !!(orderData.documentAnalysis?.aiExtractedFields || orderData.extractedData),
          datatrace: !!orderData.datatraceData,
          taxStatus: !!orderData.taxSearchData,
          titleSearch: !!orderData.titleSearchData
        };

        setAvailableSources(available);
      }
    } catch (err) {
      console.error('[CHAT] Failed to fetch order data:', err);
      // Default to no sources available except upload
      setAvailableSources({
        orderEntry: false,
        datatrace: false,
        taxStatus: false,
        titleSearch: false
      });
    }
  };

  const handleApplyAttachments = (newAttachments) => {
    setAttachments(newAttachments);
    // TODO: Save to backend for persistence
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Build enriched question with attachment content
  const buildEnrichedQuestion = (question) => {
    if (attachments.length === 0) {
      return question;
    }

    // Append attachment JSON content to the question
    const attachmentContext = attachments
      .map((att, index) => {
        let content = '';
        try {
          // For JSON content, try to parse and format
          if (att.fileType?.includes('json') || (typeof att.content === 'string' && att.content.startsWith('{'))) {
            const parsed = typeof att.content === 'string' ? JSON.parse(att.content) : att.content;
            content = JSON.stringify(parsed, null, 2);
          } else if (att.content) {
            content = att.content;
          }
        } catch {
          content = String(att.content || '');
        }

        return `
--- Attachment ${index + 1}: ${att.name} ---
${content}
---`;
      })
      .join('\n\n');

    return `${question}\n\n<attachments>${attachmentContext}\n</attachments>`;
  };

  const handleSendMessage = async () => {
    const question = inputValue.trim();

    if (!question) {
      return;
    }

    // Add user message to chat
    const userMessage = {
      type: 'user',
      content: question,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Build enriched question with attachment content
      const enrichedQuestion = buildEnrichedQuestion(question);

      // If orderId is provided, use property-chat endpoint with context
      if (orderId) {
        const processingMode = selectedModel === 'oracle' ? 'oracle' : 'single';
        const singleModel = selectedModel === 'oracle' ? 'gpt' : selectedModel;

        // Extract source IDs from attachments
        const selectedSources = attachments
          .filter(att => att.sourceId && att.sourceId !== 'uploadFile')
          .map(att => att.sourceId);

        const response = await fetch('https://sunny-analysis.messagesimproved.com/property-chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            internalId: orderId,
            question: enrichedQuestion,
            selectedSources,
            processingMode,
            singleModel,
            userId: 'test-user'
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Request failed: ${response.status}`);
        }

        const data = await response.json();

        // Add AI response based on processing mode
        if (processingMode === 'oracle') {
          // Oracle mode response (consensus)
          const aiMessage = {
            type: 'ai',
            model: 'oracle',
            content: data.consensusResponse,
            confidence: data.confidence,
            agreementScore: data.agreementScore,
            consensusSource: data.consensusSource,
            modelResponses: data.modelResponses,
            metadata: data.metadata,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, aiMessage]);
        } else {
          // Single model response
          const aiMessage = {
            type: 'ai',
            model: selectedModel,
            content: data.response || data.consensusResponse,
            metadata: data.metadata,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, aiMessage]);
        }
      }
      // Oracle mode for quick chat (no orderId)
      else if (selectedModel === 'oracle') {
        const response = await fetch('https://sunny-analysis-dev.messagesimproved.com/oracle-chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            question: enrichedQuestion,
            previousRound: null  // Round 1 has no previous round
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Request failed: ${response.status}`);
        }

        const data = await response.json();

        // Add oracle round 1 to messages
        const oracleMessage = {
          type: 'oracle',
          roundNumber: 1,
          question: question,
          responses: data.responses,
          metadata: data.metadata,
          consensusStatus: data.consensusStatus || 'diverging',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, oracleMessage]);
        setOracleRounds([oracleMessage]);
      } else {
        // Call quick chat API (staging backend)
        const response = await fetch('https://sunny-analysis-dev.messagesimproved.com/quick-chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            question: enrichedQuestion,
            model: selectedModel
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Request failed: ${response.status}`);
        }

        const data = await response.json();

        // Add AI response(s) to chat
        if (selectedModel === 'all') {
          // Multiple responses
          const aiMessage = {
            type: 'ai-multi',
            responses: data.responses,
            metadata: data.metadata,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, aiMessage]);
        } else {
          // Single response
          const aiMessage = {
            type: 'ai',
            model: selectedModel,
            content: data.response,
            metadata: data.metadata,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, aiMessage]);
        }
      }
    } catch (err) {
      console.error('[QUICK CHAT] Error:', err);
      setError(err.message || 'Failed to get response. Please try again.');

      // Add error message to chat
      const errorMessage = {
        type: 'error',
        content: err.message || 'Failed to get response. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setOracleRounds([]);
    setError(null);
  };

  // Long press handlers for send button
  const handleMouseDown = () => {
    longPressTimer.current = setTimeout(() => {
      // Long press detected - show prompt preview
      const question = inputValue.trim();
      if (question) {
        const enrichedPrompt = buildEnrichedQuestion(question);
        setPromptPreview(enrichedPrompt);
        setShowPromptModal(true);
      }
    }, 500); // 500ms long press threshold
  };

  const handleMouseUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const copyPromptToClipboard = () => {
    navigator.clipboard.writeText(promptPreview);
    alert('Prompt copied to clipboard!');
  };

  const handleReOracle = async (previousMessage) => {
    setIsLoading(true);
    setError(null);

    try {
      // Extract the previous round's responses
      const previousRound = {
        roundNumber: previousMessage.roundNumber,
        question: previousMessage.question,
        responses: previousMessage.responses
      };

      // Call oracle chat API with previous round context
      const response = await fetch('https://sunny-analysis-dev.messagesimproved.com/oracle-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question: previousMessage.question,
          previousRound: previousRound
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      const data = await response.json();

      // Add new oracle round to messages
      const oracleMessage = {
        type: 'oracle',
        roundNumber: previousMessage.roundNumber + 1,
        question: previousMessage.question,
        responses: data.responses,
        metadata: data.metadata,
        consensusStatus: data.consensusStatus || 'diverging',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, oracleMessage]);
      setOracleRounds(prev => [...prev, oracleMessage]);
    } catch (err) {
      console.error('[ORACLE] Re-Oracle Error:', err);
      setError(err.message || 'Failed to re-oracle. Please try again.');
      const errorMessage = {
        type: 'error',
        content: err.message || 'Failed to re-oracle. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleAcceptConsensus = (oracleMessage) => {
    // User accepts the consensus - could save or finalize here
    const consensusMessage = {
      type: 'user',
      content: `✓ Accepted consensus from Round ${oracleMessage.roundNumber}`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, consensusMessage]);
  };

  const getModelLabel = (model) => {
    const labels = {
      gpt: 'GPT-4o',
      claude: 'Claude 3.5 Sonnet',
      gemini: 'Gemini 2.5 Flash',
      all: 'All Models (Multi-AI)',
      oracle: 'Oracle Mode'
    };
    return labels[model] || model;
  };

  const getModelIcon = (model) => {
    const icons = {
      gpt: '●',      // Solid circle (will be styled via CSS)
      claude: '●',   // Solid circle (will be styled via CSS)
      gemini: '●',   // Solid circle (will be styled via CSS)
      all: '◆',      // Diamond shape for "all models"
      oracle: '◉'    // Circle with dot for "oracle"
    };
    return icons[model] || '○';
  };

  const renderMessage = (message, index) => {
    if (message.type === 'user') {
      return (
        <div key={index} className="quick-chat-message quick-chat-message-user">
          <div className="quick-chat-message-content">
            <div className="quick-chat-message-text">{message.content}</div>
            <div className="quick-chat-message-time">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
      );
    }

    if (message.type === 'error') {
      return (
        <div key={index} className="quick-chat-message quick-chat-message-error">
          <div className="quick-chat-message-content">
            <div className="quick-chat-error-icon">⚠️</div>
            <div className="quick-chat-message-text">{message.content}</div>
            <div className="quick-chat-message-time">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
      );
    }

    if (message.type === 'ai') {
      return (
        <div key={index} className="quick-chat-message quick-chat-message-ai">
          <div className="quick-chat-message-avatar">
            {getModelIcon(message.model)}
          </div>
          <div className="quick-chat-message-content">
            <div className="quick-chat-model-label">
              {getModelLabel(message.model)}
            </div>
            <div className="quick-chat-message-text">{message.content}</div>
            {message.metadata && (
              <div className="quick-chat-metadata">
                {message.metadata.tokens && <span>{message.metadata.tokens} tokens</span>}
                {message.metadata.latency && <span>{message.metadata.latency.toFixed(2)}s</span>}
                {message.metadata.cost && <span>${message.metadata.cost.toFixed(4)}</span>}
              </div>
            )}
            <div className="quick-chat-message-time">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
      );
    }

    if (message.type === 'ai-multi') {
      // Define model order: gemini, claude, gpt
      const modelOrder = ['gemini', 'claude', 'gpt'];
      const orderedResponses = modelOrder
        .filter(model => message.responses[model])
        .map(model => [model, message.responses[model]]);

      return (
        <div key={index} className="quick-chat-message quick-chat-message-multi">
          <div className="quick-chat-multi-header">
            <span className="quick-chat-multi-icon">⸫</span>
            <span className="quick-chat-multi-title">Multi-AI Responses</span>
          </div>
          <div className="quick-chat-multi-responses">
            {orderedResponses.map(([modelKey, modelData]) => (
              <div key={modelKey} className="quick-chat-multi-response">
                <div className="quick-chat-multi-response-header">
                  <span className="quick-chat-multi-model-icon">{getModelIcon(modelKey)}</span>
                  <span className="quick-chat-multi-model-name">{getModelLabel(modelKey)}</span>
                </div>
                <div className="quick-chat-multi-response-text">{modelData.response}</div>
                {modelData.metadata && (
                  <div className="quick-chat-metadata">
                    {modelData.metadata.tokens && <span>{modelData.metadata.tokens} tokens</span>}
                    {modelData.metadata.latency && <span>{modelData.metadata.latency.toFixed(2)}s</span>}
                    {modelData.metadata.cost && <span>${modelData.metadata.cost.toFixed(4)}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
          {message.metadata && (
            <div className="quick-chat-multi-summary">
              <strong>Total:</strong> {message.metadata.totalTokens} tokens ·
              ${message.metadata.totalCost?.toFixed(4)} ·
              {message.metadata.totalLatency?.toFixed(2)}s
            </div>
          )}
          <div className="quick-chat-message-time">
            {new Date(message.timestamp).toLocaleTimeString()}
          </div>
        </div>
      );
    }

    if (message.type === 'oracle') {
      // Define model order: gemini, claude, gpt
      const modelOrder = ['gemini', 'claude', 'gpt'];
      const orderedResponses = modelOrder
        .filter(model => message.responses[model])
        .map(model => [model, message.responses[model]]);

      return (
        <div key={index} className="quick-chat-message quick-chat-message-oracle">
          <div className="quick-chat-oracle-header">
            <span className="quick-chat-oracle-icon">⚛</span>
            <span className="quick-chat-oracle-title">Oracle Round {message.roundNumber}</span>
          </div>
          <div className="quick-chat-oracle-responses">
            {orderedResponses.map(([modelKey, modelData]) => (
              <div key={modelKey} className="quick-chat-oracle-column">
                <div className="quick-chat-oracle-column-header">
                  <span className="quick-chat-oracle-model-icon">{getModelIcon(modelKey)}</span>
                  <span className="quick-chat-oracle-model-name">{getModelLabel(modelKey)}</span>
                  <span className="quick-chat-oracle-round-label">Round {message.roundNumber}</span>
                </div>
                <div className="quick-chat-oracle-response-text">{modelData.response}</div>
                {modelData.metadata && (
                  <div className="quick-chat-metadata">
                    {modelData.metadata.tokens && <span>{modelData.metadata.tokens} tokens</span>}
                    {modelData.metadata.latency && <span>{modelData.metadata.latency.toFixed(2)}s</span>}
                    {modelData.metadata.cost && <span>${modelData.metadata.cost.toFixed(4)}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
          {message.metadata && (
            <div className="quick-chat-oracle-summary">
              <strong>Total:</strong> {message.metadata.totalTokens} tokens ·
              ${message.metadata.totalCost?.toFixed(4)} ·
              {message.metadata.totalLatency?.toFixed(2)}s
            </div>
          )}
          <div className="quick-chat-oracle-actions">
            <button
              className="quick-chat-oracle-button quick-chat-oracle-button-primary"
              onClick={() => handleReOracle(message)}
            >
              ⚛ Re-Oracle
            </button>
          </div>
          <div className="quick-chat-message-time">
            {new Date(message.timestamp).toLocaleTimeString()}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <AttachmentSelector
        isOpen={showAttachmentModal}
        onClose={() => setShowAttachmentModal(false)}
        attachments={attachments}
        onApply={handleApplyAttachments}
        availableSources={availableSources}
        orderId={orderId}
      />

      {/* Prompt Preview Modal */}
      {showPromptModal && (
        <div className="prompt-preview-modal-overlay" onClick={() => setShowPromptModal(false)}>
          <div className="prompt-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="prompt-preview-header">
              <h3>📝 Full Prompt Preview</h3>
              <button className="prompt-preview-close" onClick={() => setShowPromptModal(false)}>×</button>
            </div>
            <div className="prompt-preview-body">
              <p className="prompt-preview-description">
                This is the complete prompt that will be sent to {getModelLabel(selectedModel)}:
              </p>
              <pre className="prompt-preview-content">{promptPreview}</pre>
              <div className="prompt-preview-stats">
                <span>Characters: {promptPreview.length}</span>
                <span>•</span>
                <span>Lines: {promptPreview.split('\n').length}</span>
                {attachments.length > 0 && (
                  <>
                    <span>•</span>
                    <span>Attachments: {attachments.length}</span>
                  </>
                )}
              </div>
            </div>
            <div className="prompt-preview-footer">
              <button className="prompt-preview-copy-btn" onClick={copyPromptToClipboard}>
                📋 Copy to Clipboard
              </button>
              <button className="prompt-preview-close-btn" onClick={() => setShowPromptModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="quick-chat-container">
        {/* Model selector sidebar */}
        <div className="quick-chat-sidebar">
        <div className="quick-chat-sidebar-header">
          <h3>Select Model</h3>
        </div>
        <div className="quick-chat-model-options">
          <button
            className={`quick-chat-model-button ${selectedModel === 'oracle' ? 'active' : ''}`}
            onClick={() => setSelectedModel('oracle')}
          >
            <span className="quick-chat-model-icon">⚛</span>
            <span className="quick-chat-model-name">Oracle Mode</span>
          </button>
          <button
            className={`quick-chat-model-button ${selectedModel === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedModel('all')}
          >
            <span className="quick-chat-model-icon">⸫</span>
            <span className="quick-chat-model-name">All Models</span>
          </button>
          <button
            className={`quick-chat-model-button ${selectedModel === 'gemini' ? 'active' : ''}`}
            onClick={() => setSelectedModel('gemini')}
          >
            <span className="quick-chat-model-icon">◆</span>
            <span className="quick-chat-model-name">Gemini 2.5 Flash</span>
          </button>
          <button
            className={`quick-chat-model-button ${selectedModel === 'claude' ? 'active' : ''}`}
            onClick={() => setSelectedModel('claude')}
          >
            <span className="quick-chat-model-icon">◆</span>
            <span className="quick-chat-model-name">Claude 3.5 Sonnet</span>
          </button>
          <button
            className={`quick-chat-model-button ${selectedModel === 'gpt' ? 'active' : ''}`}
            onClick={() => setSelectedModel('gpt')}
          >
            <span className="quick-chat-model-icon">◆</span>
            <span className="quick-chat-model-name">GPT-4o</span>
          </button>
        </div>

        {/* Attachments button (always visible) */}
        <div className="quick-chat-sidebar-section">
          <div className="quick-chat-sidebar-section-header">
            <h4>Data Sources</h4>
          </div>
          <button
            className="quick-chat-attachments-button"
            onClick={() => setShowAttachmentModal(true)}
          >
            <span className="quick-chat-attachments-icon">📎</span>
            <span className="quick-chat-attachments-text">
              Attachments ({attachments.length})
            </span>
          </button>

          {/* Display attachment bubbles */}
          {attachments.length > 0 && (
            <div className="quick-chat-attachment-bubbles">
              {attachments.map((att, index) => (
                <div key={index} className="quick-chat-attachment-bubble">
                  <span className="quick-chat-attachment-icon">
                    {att.type === 'uploadFile' && '📎'}
                    {att.type === 'orderEntry' && '📄'}
                    {att.type === 'datatrace' && '🏘️'}
                    {att.type === 'taxStatus' && '💰'}
                    {att.type === 'titleSearch' && '📋'}
                  </span>
                  <span className="quick-chat-attachment-name" title={att.name}>
                    {att.name}
                  </span>
                  <button
                    className="quick-chat-attachment-remove"
                    onClick={() => removeAttachment(index)}
                    title="Remove attachment"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="quick-chat-sidebar-footer">
          <button className="quick-chat-clear-button" onClick={handleClearChat}>
            🗑️ Clear Chat
          </button>
        </div>
      </div>

      {/* Main chat area */}
      <div className="quick-chat-main">
        <div className="quick-chat-messages">
          {messages.length === 0 && (
            <div className="quick-chat-welcome">
              <div className="quick-chat-welcome-icon">
                {getModelIcon(selectedModel)}
              </div>
              <h2>Quick Chat</h2>
              <p>Test individual AI models or compare responses across all models.</p>
              <div className="quick-chat-suggestions">
                <p className="quick-chat-suggestions-title">Try asking:</p>
                <button
                  className="quick-chat-suggestion"
                  onClick={() => setInputValue("What is a Schedule B exception?")}
                >
                  "What is a Schedule B exception?"
                </button>
                <button
                  className="quick-chat-suggestion"
                  onClick={() => setInputValue("Explain the difference between a lien and an easement")}
                >
                  "Explain the difference between a lien and an easement"
                </button>
                <button
                  className="quick-chat-suggestion"
                  onClick={() => setInputValue("What is 2 + 2?")}
                >
                  "What is 2 + 2?"
                </button>
              </div>
            </div>
          )}

          {messages.map((message, index) => renderMessage(message, index))}

          {isLoading && (
            <div className="quick-chat-message quick-chat-message-ai quick-chat-message-loading">
              <div className="quick-chat-message-avatar">
                {getModelIcon(selectedModel)}
              </div>
              <div className="quick-chat-message-content">
                <div className="quick-chat-loading-dots">
                  <span className="quick-chat-loading-dot"></span>
                  <span className="quick-chat-loading-dot"></span>
                  <span className="quick-chat-loading-dot"></span>
                </div>
                <div className="quick-chat-loading-text">
                  {selectedModel === 'all'
                    ? 'Consulting all models...'
                    : `${getModelLabel(selectedModel)} is thinking...`}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="quick-chat-input-area">
          {error && (
            <div className="quick-chat-error-banner">
              ⚠️ {error}
            </div>
          )}

          <div className="quick-chat-input-wrapper">
            <textarea
              ref={inputRef}
              className="quick-chat-input"
              placeholder={`Ask ${getModelLabel(selectedModel)} anything...`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              rows={1}
            />
            <button
              className="quick-chat-send-button"
              onClick={handleSendMessage}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleMouseDown}
              onTouchEnd={handleMouseUp}
              disabled={isLoading || !inputValue.trim()}
              title="Click to send, long-press to preview prompt"
            >
              {isLoading ? '⏳' : '↑'}
            </button>
          </div>

          <div className="quick-chat-input-footer">
            <span className="quick-chat-model-indicator">
              {getModelIcon(selectedModel)} {getModelLabel(selectedModel)}
            </span>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default QuickChat;
